/*  Greatly Social – Facebook modal wrapper (v2.6)
    ───────────────────────────────────────────────────────────────
    • Uses plain <input type="checkbox"> for reliable clicks.
    • Keeps pageId as a *string*. FB page IDs are opaque identifiers and
      modern ones exceed JS's safe-integer range (2^53); Number() would
      corrupt them, so the DELETE could never match and the page would
      reappear — the "can't delete Facebook" bug. String throughout.
    • Correct handler prop name: handleFbCheckboxChange.
*/

import React from 'react'
import { Modal, Button, Table, Image } from 'react-bootstrap'

export default function FacebookModals ({
  /* state props coming from useAccountHandlers */
  /* add / connect flow */
  showFbPagesModal,
  setShowFbPagesModal,
  fbPages,
  selectedFbPageIds,
  handleFbCheckboxChange,           // ← correct prop
  handleConnectSelectedPages,

  /* delete flow */
  showDeleteModal,
  setShowDeleteModal,
  handleDeleteSelectedPages,
  showDeleteConfirmModal,
  setShowDeleteConfirmModal,
  handleDeleteSelectedPagesConfirm
}) {
  /* ───────────────────────── 1. Connect Pages ───────────────────────── */
  const PagesModal = (
    <Modal
      show={showFbPagesModal}
      onHide={() => setShowFbPagesModal(false)}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Facebook Pages</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Table hover responsive className="align-middle mb-0">
          <tbody>
            {fbPages.map(p => {
              const pageId   = String(p.id ?? p.fb_page_id ?? '') // opaque string ID
              const pageName = p.name || p.fb_page_name
              const avatar   =
                    p.picture?.data?.url ||
                    p.pictureUrl ||
                    p.fb_page_picture_url ||
                    '/img/default-avatar.png'

              const checked = selectedFbPageIds.includes(pageId)

              return (
                <tr key={pageId}>
                  <td style={{ width: 50 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={checked}
                      onChange={() => handleFbCheckboxChange(pageId)}
                    />
                  </td>
                  <td style={{ width: 50 }}>
                    <Image src={avatar} roundedCircle width={40} height={40} />
                  </td>
                  <td>{pageName}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowFbPagesModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConnectSelectedPages}
          disabled={!selectedFbPageIds.length}
        >
          Connect&nbsp;Selected
        </Button>
      </Modal.Footer>
    </Modal>
  )

  /* ──────────────────────── 2. Delete – step 1 ──────────────────────── */
  const DeleteSelect = (
    <Modal
      show={showDeleteModal}
      onHide={() => setShowDeleteModal(false)}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Remove Facebook Pages</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {fbPages.length ? (
          <Table hover responsive className="align-middle mb-0">
            <tbody>
              {fbPages.map(p => {
                const pageId   = String(p.id ?? p.fb_page_id ?? '') // opaque string ID
                const pageName = p.name || p.fb_page_name
                const avatar   = p.pictureUrl || p.fb_page_picture_url || '/img/default-avatar.png'
                const checked  = selectedFbPageIds.includes(pageId)

                return (
                  <tr key={pageId}>
                    <td style={{ width: 50 }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        onChange={() => handleFbCheckboxChange(pageId)}
                      />
                    </td>
                    <td style={{ width: 50 }}>
                      <Image src={avatar} roundedCircle width={40} height={40} />
                    </td>
                    <td>{pageName}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        ) : (
          <p className="mb-0">No Facebook pages are currently connected.</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDeleteSelectedPages}
          disabled={!selectedFbPageIds.length}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  )

  /* ──────────────────────── 3. Delete – confirm ─────────────────────── */
  const DeleteConfirm = (
    <Modal
      show={showDeleteConfirmModal}
      onHide={() => setShowDeleteConfirmModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Delete selected pages?</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        This will permanently remove the selected Facebook pages from your
        Greatly&nbsp;Social account. You can re-connect them later if you change
        your mind.
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteSelectedPagesConfirm}>
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  )

  /* ─────────────────────────── render ─────────────────────────── */
  return (
    <>
      {PagesModal}
      {DeleteSelect}
      {DeleteConfirm}
    </>
  )
}